import { ReactNode, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "./store";
import {
  useCurrentFrameLocale,
  useCurrentFrameSettings,
  useIsFullscreen,
  useLauncher,
  useSyncSuggestions,
  useTemporaryBlocker,
} from "./hooks";
import { setIsPreviewVisible } from "./slices/launcherSlice";
import { LauncherSettings } from "./types/core";
import posthog from "posthog-js";
import { ensureAnalyticsInitialized } from "./analytics";
import { createSelector } from "@reduxjs/toolkit";
import { AnyMessage } from "./chat/types";

import ScrollLock from "./ScrollLock";
import useViewportHeight from "./useViewportHeight";
import { Trans, useTranslation } from "react-i18next";
import "core-js/features/array/find-last";
import { X } from "lucide-react";
import { sanitize } from "./sanitize";

// NOTE(liamvdv): when changing between preact and react (preact/compat), check
//                the SVG elements, they are no longer normalized by react to camelCase.
//                e.g. fillRule -> fill-rule, clipRule -> clip-rule, strokeWidth -> stroke-width
//                https://preactjs.com/guide/v10/differences-to-react/#svg-inside-jsx

function getLauncherStyle(
  settings: LauncherSettings | undefined,
  userSettings: {
    sideMargin: string | null;
    bottomMargin: string | null;
    side: "left" | "right" | null;
  }
) {
  const style: React.CSSProperties = {
    backgroundColor: settings?.button_color ?? "#FFF",
    bottom: userSettings.bottomMargin ?? settings?.bottom_margin ?? "1.5rem",
  };

  // User-set side takes precedence over server alignment
  const effectiveSide = userSettings.side ?? settings?.alignment;
  const effectiveSideMargin =
    userSettings.sideMargin ?? settings?.side_margin ?? "1.5rem";

  if (effectiveSide === "left") {
    style.left = effectiveSideMargin;
    style.right = undefined;
  } else if (effectiveSide === "right") {
    style.right = effectiveSideMargin;
    style.left = undefined;
  }
  return style;
}

function showElement(yes: boolean) {
  return yes
    ? "opacity-100 pointer-events-auto"
    : "opacity-0 pointer-events-none";
}

const useBlockedSuggestions = () => {
  const frameId = useAppSelector((state) => state.config.data.frameId);
  const [blockedSuggestions, setBlockedSuggestions] = useTemporaryBlocker(
    `${frameId}.blockedSuggestions`,
    isIframe() ? 0 : 30 * 24 * 60 * 60
  );
  return [blockedSuggestions, setBlockedSuggestions] as const;
};

function usePositioning(
  isOpen: boolean,
  isPreviewVisible: boolean,
  bubbleHeigth: number = 48,
  heightLimit: number = 704,
  widthLimit: number = 400
) {
  const { data, status } = useCurrentFrameSettings();
  const settings = data?.launcher_settings;
  const sideMargin = useAppSelector((state) => state.launcher.sideMargin);
  const bottomMargin = useAppSelector((state) => state.launcher.bottomMargin);
  const side = useAppSelector((state) => state.launcher.side);
  const isFullscreen = useIsFullscreen();
  const bubbleIsOpen = isOpen ? (isFullscreen ? "hidden" : "block") : "block";
  const style = getLauncherStyle(settings, {
    sideMargin,
    bottomMargin,
    side,
  });
  const launcherToFrameGap = 24;
  const bottom = `calc(${style.bottom} + ${bubbleHeigth}px + ${launcherToFrameGap}px)`;

  useViewportHeight("--botbrains-vh");

  // MAX value for z-index is 2147483647
  // do not set max value, only set if isOpen
  const zIndexBase = isOpen ? "z-[2147483647]" : "z-[2147482998]";
  const zIndexTrump = isOpen ? "z-[2147483647]" : "z-[2147482999]";
  const isLeft = (side ?? settings?.alignment) === "left";
  return {
    launcher: {
      className: `fixed w-12 h-12 rounded-full cursor-pointer transition-transform duration-300 flex items-center justify-center ${bubbleIsOpen} ${zIndexBase} select-none`,
      style: style,
    },
    frame: {
      // NOTE(liamvdv): xs and -xs are equivalent to isFullscreen
      // !inset-0 to overwrite left/right positining in fullscreen from ...style
      className: `fixed ${isFullscreen ? "!inset-0" : "rounded-lg"} w-full ${showElement(isOpen)} shadow-[0_0_15px_rgba(0,0,0,0.1)] transition-opacity duration-250 ease-[cubic-bezier(0, 1.2, 1, 1)] origin-bottom-right ${zIndexTrump} overflow-hidden`,
      style: {
        ...style,
        maxWidth: isFullscreen ? "100%" : `${widthLimit}px`,
        maxHeight: isFullscreen
          ? "" // NOTE(liamvdv): On Safari, the bottom toolbar can collapse on scroll. This leaves white space. With max-height to 100%, the content will not expand to fill the space. Without it, it will.
          : `min(${heightLimit}px, calc(100vh - ${bottom} - 16px)`,
        top: isFullscreen ? "auto" : `max(24px,calc(100vh-${heightLimit}px))`,
        bottom: bottom,
        backgroundColor: "#FFF",
        height: isFullscreen ? "calc(var(--botbrains-vh, 1vh) * 100)" : "100vh",
      },
    },
    preview: {
      // NOTE(liamvdv): xs and -xs are equivalent to isFullscreen
      className: `fixed ${showElement(isPreviewVisible)} ${isFullscreen ? "" : "rounded-lg"}  transition-opacity duration-250 ease-[cubic-bezier(0, 1.2, 1, 1)] origin-bottom-right ${zIndexBase} ${isLeft ? "mr-2" : "ml-2"}`,
      style: {
        ...style,
        top: "auto",
        bottom: bottom,
        backgroundColor: "transparent",
      },
    },
    status: status,
  };
}

function CloseBubbleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke-width="2.5"
      stroke="currentColor"
      className="w-6 h-6"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

function OpenBubbleIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-6 h-6"
    >
      <path
        fill-rule="evenodd" // preact specific
        d="M4.848 2.771A49.144 49.144 0 0112 2.25c2.43 0 4.817.178 7.152.52 1.978.292 3.348 2.024 3.348 3.97v6.02c0 1.946-1.37 3.678-3.348 3.97a48.901 48.901 0 01-3.476.383.39.39 0 00-.297.17l-2.755 4.133a.75.75 0 01-1.248 0l-2.755-4.133a.39.39 0 00-.297-.17 48.9 48.9 0 01-3.476-.384c-1.978-.29-3.348-2.024-3.348-3.97V6.741c0-1.946 1.37-3.68 3.348-3.97zM6.75 8.25a.75.75 0 01.75-.75h9a.75.75 0 010 1.5h-9a.75.75 0 01-.75-.75zm.75 2.25a.75.75 0 000 1.5H12a.75.75 0 000-1.5H7.5z"
        clip-rule="evenodd"
      />
    </svg>
  );
}

function BubbleIcon({
  isOpen,
  onClick,
}: {
  isOpen: boolean;
  onClick: () => void;
}) {
  const { data: frameSettings } = useCurrentFrameSettings();
  const assistantImage = frameSettings?.assistant_image_url;
  const isPreviewVisible = useAppSelector(
    (state) => state.launcher.isPreviewVisible
  );

  return (
    <BubbleIconContainer
      isOpen={isOpen}
      onClick={onClick}
      bgClassName="bg-launcher"
    >
      <div className="relative w-full h-full flex items-center justify-center">
        {isOpen ? <CloseBubbleIcon /> : <OpenBubbleIcon />}
        {!isOpen && !isPreviewVisible && assistantImage && (
          <img
            src={assistantImage}
            alt="Assistant"
            className="absolute -top-1 -left-1 w-5 h-5 rounded-full object-cover border-2 border-white shadow-sm"
          />
        )}
      </div>
    </BubbleIconContainer>
  );
}

function BubbleIconContainer({
  isOpen,
  onClick,
  children,
  bgClassName = "bg-launcher",
}: {
  isOpen: boolean;
  onClick: () => void;
  children: ReactNode;
  bgClassName: string;
}) {
  const {
    launcher: { className, style },
    status,
  } = usePositioning(isOpen, false);
  return status === "succeeded" ? (
    <button
      aria-label="Chat launcher"
      aria-expanded={isOpen}
      className={className}
      style={style}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.1)";
        e.currentTarget.style.transition = "transform 0.2s ease-in";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.transition = "transform 0.3s ease-out";
      }}
      onMouseDown={(e) => {
        e.currentTarget.style.transform = "scale(0.9)";
        e.currentTarget.style.transition = "transform 0.2s ease";
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        e.currentTarget.style.transition = "transform 0.3s ease";
      }}
      onClick={onClick}
    >
      <div
        className={`rounded-full w-full h-full text-white flex items-center justify-center ${bgClassName}`}
      >
        {children}
      </div>
    </button>
  ) : (
    <></>
  );
}

export function isIframe() {
  return window !== window.top;
}

const hasNonInitialMessages = createSelector(
  [(state) => state.chat.messages],
  (messages) => messages.filter((m: AnyMessage) => !m.isInitial).length > 0
);

export const useSuggestions = () => {
  const dispatch = useAppDispatch();
  const suggestions = useAppSelector((state) => state.prompt.suggestions);
  const { data: frameSettings } = useCurrentFrameSettings();
  const isEnabled = frameSettings?.show_popup;
  const launcherOpenedOnce = useAppSelector(
    (state) => state.launcher.openedOnce
  );
  const isVisible = useAppSelector((state) => state.launcher.isPreviewVisible);
  const [dismissedBefore, setDismissedBefore] = useBlockedSuggestions();
  const hasActiveConversation = useAppSelector(hasNonInitialMessages);
  const shouldShow =
    isEnabled &&
    !launcherOpenedOnce &&
    !dismissedBefore &&
    !hasActiveConversation; // don't annoy users with suggestions
  const positioning = usePositioning(false, isVisible);
  const previewDelayMs = useAppSelector(
    (state) => state.config.data.previewDelayMs
  );

  useSyncSuggestions();

  // Handle visibility based on dismissed state
  useEffect(() => {
    if (dismissedBefore) {
      dispatch(setIsPreviewVisible(false));
    }
  }, [dismissedBefore, dispatch]);

  // Handle visibility based on should show state
  useEffect(() => {
    if (!shouldShow) {
      dispatch(setIsPreviewVisible(shouldShow));
    }
  }, [shouldShow, dispatch]);

  // Show preview after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldShow) {
        dispatch(setIsPreviewVisible(true));
      }
    }, previewDelayMs);

    return () => clearTimeout(timer);
  }, [shouldShow, dispatch]);

  const discard = () => {
    ensureAnalyticsInitialized();
    posthog.capture("launcher_preview_discard", {
      sincePageLoad: performance.now(),
    });
    dispatch(setIsPreviewVisible(false));
    setDismissedBefore(true);
  };

  const startChatAndHide = (suggestion: string) => {
    dispatch(setIsPreviewVisible(false));
    window.$botbrains.do("message.send", suggestion);
  };

  const openChatAndHide = () => {
    dispatch(setIsPreviewVisible(false));
    window.$botbrains.do("chat.open");
  };

  return {
    suggestions,
    isVisible,
    shouldShow,
    positioning,
    frameSettings,
    discard,
    startChatAndHide,
    openChatAndHide,
  };
};

const usePreviewText = () => {
  const { t } = useTranslation();
  const { data: settings, status: settingsStatus } = useCurrentFrameSettings();
  // the last message is the most recent isInitial message of the assistant.
  const messages = useAppSelector((state) => state.chat.messages);
  const { locale, status: localeState } = useCurrentFrameLocale();
  let text = locale?.messages[0]?.content;
  const lastInitalAssistant = messages.findLast(
    (m) => m.isInitial && m.role === "assistant"
  );
  if (lastInitalAssistant) text = lastInitalAssistant.content;
  return {
    isEnabled:
      localeState === "succeeded" &&
      settingsStatus === "succeeded" &&
      settings?.show_popup,
    name: settings?.assistant_name ?? t("assistant"),
    image: settings?.assistant_image_url,
    text,
  };
};

const GrayedCorner = ({ isVisible }: { isVisible: boolean }) => {
  const frameSettings = useCurrentFrameSettings().data?.launcher_settings;
  const side = useAppSelector((state) => state.launcher.side);
  const isRight = (side ?? frameSettings?.alignment) === "right";

  return (
    <div
      className={`fixed pointer-events-none w-[700px] h-[700px] z-[2147482998] bottom-0 transition-opacity ease-in duration-200 ${isVisible ? "opacity-100" : "opacity-0"}`}
      style={{
        ...(isRight
          ? { right: "0px", left: undefined }
          : { left: "0px", right: undefined }),
        background: `radial-gradient(at ${isRight ? "right" : "left"} bottom, rgba(29, 39, 54, 0.15) 0%, rgba(29, 39, 54, 0) 75%)`,
      }}
    />
  );
};

export function PreviewPopup() {
  const {
    suggestions,
    isVisible,
    positioning,
    frameSettings,
    discard,
    startChatAndHide,
    openChatAndHide,
  } = useSuggestions();
  const { name, text, isEnabled, image } = usePreviewText();
  const getStyles = (isRight: boolean) => ({
    liStyle: isRight ? "" : "",
    ulStyle: isRight ? "items-end" : "items-start",
  });
  const makeVisible = isVisible
    ? "opacity-100 pointer-events-auto"
    : "opacity-0 pointer-events-none";

  const isMobile = useIsFullscreen();
  const largeScreenVisible = isMobile && isEnabled ? "hidden" : "visible";

  const userSettingsSide = useAppSelector((state) => state.launcher.side);
  const isRight =
    (userSettingsSide ?? frameSettings?.launcher_settings.alignment) ===
    "right";

  const { liStyle, ulStyle } = getStyles(isRight);

  if (!isVisible) return null;
  return (
    <div
      className={positioning.preview.className}
      style={positioning.preview.style}
    >
      <div
        className={`flex flex-col gap-3 ${ulStyle} ${makeVisible} transition-opacity duration-500 ease-in origin-bottom-right z-[2147483000] max-w-[400px]`}
      >
        <div
          className="rounded-full p-1 cursor-pointer hover:scale-110 transition-transform duration-300 ease-in-out bg-preview text-preview-text"
          onClick={discard}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="w-5 h-5"
          >
            <path
              fillRule="evenodd"
              d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z"
              clipRule="evenodd"
            />
          </svg>
        </div>

        <div
          className={`w-full cursor-pointer hover:scale-105 transition-transform duration-300 ${makeVisible} ease-in-out py-2 px-4 flex-shrink-0 text-sm rounded-[24px] ${liStyle} bg-chat shadow-lg `}
          onClick={openChatAndHide}
        >
          <div className="flex gap-2 p-2 z-1 items-center">
            {image && (
              <img
                src={image}
                alt="Assistant"
                className="w-14 h-14 rounded-full flex-shrink-0 object-cover"
              />
            )}
            <div className="flex-1 min-w-0">
              <span className="font-medium text-brand">{name}</span>
              <div
                className="text-gray-700 text-chat-text max-w-full min-w-0"
                dangerouslySetInnerHTML={{
                  __html: sanitize(text ?? ""),
                }}
              />
            </div>
          </div>
        </div>

        <div
          className={`flex flex-wrap gap-2 justify-start px-2 ${largeScreenVisible}`}
        >
          {[...(suggestions || [])].map((suggestion) => (
            <button
              key={suggestion}
              onClick={() => startChatAndHide(suggestion)}
              className={`cursor-pointer hover:scale-105 transition-transform duration-300 ease-in-out w-fit py-2 px-4 shadow flex-shrink-0 text-sm leading-4 rounded-[24px] ${liStyle} bg-preview text-preview-text`}
            >
              <p>{suggestion}</p>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Frame({
  isOpen,
  children,
  widthLimit = undefined,
  heightLimit = undefined,
}: {
  isOpen: boolean;
  children: React.ReactNode;
  widthLimit?: number;
  heightLimit?: number;
}) {
  const {
    status,
    frame: { className, style },
  } = usePositioning(isOpen, false, undefined, heightLimit, widthLimit);

  const isScrollLockActive = useIsFullscreen() && isOpen;
  if (status !== "succeeded") return <></>;
  return (
    <ScrollLock isDisabled={!isScrollLockActive}>
      <div className={`${className}`} style={style}>
        {children}
      </div>
    </ScrollLock>
  );
}

function ModeLauncher({ children }: { children: ReactNode }) {
  const { isOpen, isShow, toggle, isPreviewVisible, isExpanded } =
    useLauncher();
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const widthLimit = isExpanded ? Math.min(688, windowWidth - 64) : 400;
  const heightLimit = isExpanded ? Math.max(704, windowHeight - 48) : 704; // Dynamic height for expanded mode with top margin
  return (
    // Require that SDK isLoaded / processed queue -- because frame might be manually hiden by SDK.
    <div className={showElement(isShow) + " antialiased"}>
      <GrayedCorner isVisible={isShow && (isOpen || isPreviewVisible)} />
      <BubbleIcon isOpen={isOpen} onClick={toggle} />
      <PreviewPopup />
      <Frame isOpen={isOpen} widthLimit={widthLimit} heightLimit={heightLimit}>
        {children}
      </Frame>
    </div>
  );
}

function ModeFullscreen({ children }: { children: ReactNode }) {
  // NOTE(liamvdv): we cannot use vh-100 because dynamic browser UI elements are not accounted for (on safari blocking input).
  //                We need to use a fixed height to ensure the frame is always visible.
  //                Why not dvh? Works 95% of mobile phones, but only 85% of browsers (Firefox doesn't support).
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const updateHeight = () => setHeight(window.innerHeight);
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <div className="w-[100vw] fixed inset-0" style={{ height: `${height}px` }}>
      {children}
    </div>
  );
}

// This function is used to reparent the shadow host to the element specified in the config.
// It is idempotent and will only run once.
function reparentShadowHostOnceTo(selector: string) {
  const reactHost = document.querySelector("#botbrains-shadow-host");
  const element = document.querySelector(selector);
  if (!reactHost) {
    console.error("reparentShadowHostOnceTo: reactHost not found");
    return false;
  }
  if (!element) {
    console.error(
      `reparentShadowHostOnceTo: element (selector: ${selector}) not found`
    );
    return false;
  }
  // Check that reactHost is not already a child of element
  if (!element.contains(reactHost)) {
    element.appendChild(reactHost);
  }
  return element.contains(reactHost);
}

function ModeInline({ children }: { children: ReactNode }) {
  const [success, setSuccess] = useState(false);
  const mode = useAppSelector((state) => state.config.data.mode);
  const selector = useAppSelector((state) => state.config.data.selector);
  // assert correct use.
  if (mode !== "inline")
    throw new Error("ModeInline can only be used in inline mode.");

  useEffect(() => {
    if (!selector) throw new Error("selector is required for inline mode.");
    const ok = reparentShadowHostOnceTo(selector);
    if (ok) setSuccess(true);
  }, [selector]);
  if (!success) return <></>;
  else return <div className="h-full w-full">{children}</div>;
}

function WhatsAppIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      viewBox="0 0 308 308"
      xmlSpace="preserve"
      className="w-6 h-6 fill-current"
    >
      <g>
        <path d="M227.904,176.981c-0.6-0.288-23.054-11.345-27.044-12.781c-1.629-0.585-3.374-1.156-5.23-1.156   c-3.032,0-5.579,1.511-7.563,4.479c-2.243,3.334-9.033,11.271-11.131,13.642c-0.274,0.313-0.648,0.687-0.872,0.687   c-0.201,0-3.676-1.431-4.728-1.888c-24.087-10.463-42.37-35.624-44.877-39.867c-0.358-0.61-0.373-0.887-0.376-0.887   c0.088-0.323,0.898-1.135,1.316-1.554c1.223-1.21,2.548-2.805,3.83-4.348c0.607-0.731,1.215-1.463,1.812-2.153   c1.86-2.164,2.688-3.844,3.648-5.79l0.503-1.011c2.344-4.657,0.342-8.587-0.305-9.856c-0.531-1.062-10.012-23.944-11.02-26.348   c-2.424-5.801-5.627-8.502-10.078-8.502c-0.413,0,0,0-1.732,0.073c-2.109,0.089-13.594,1.601-18.672,4.802   c-5.385,3.395-14.495,14.217-14.495,33.249c0,17.129,10.87,33.302,15.537,39.453c0.116,0.155,0.329,0.47,0.638,0.922   c17.873,26.102,40.154,45.446,62.741,54.469c21.745,8.686,32.042,9.69,37.896,9.69c0.001,0,0.001,0,0.001,0   c2.46,0,4.429-0.193,6.166-0.364l1.102-0.105c7.512-0.666,24.02-9.22,27.775-19.655c2.958-8.219,3.738-17.199,1.77-20.458   C233.168,179.508,230.845,178.393,227.904,176.981z" />
        <path d="M156.734,0C73.318,0,5.454,67.354,5.454,150.143c0,26.777,7.166,52.988,20.741,75.928L0.212,302.716   c-0.484,1.429-0.124,3.009,0.933,4.085C1.908,307.58,2.943,308,4,308c0.405,0,0.813-0.061,1.211-0.188l79.92-25.396   c21.87,11.685,46.588,17.853,71.604,17.853C240.143,300.27,308,232.923,308,150.143C308,67.354,240.143,0,156.734,0z    M156.734,268.994c-23.539,0-46.338-6.797-65.936-19.657c-0.659-0.433-1.424-0.655-2.194-0.655c-0.407,0-0.815,0.062-1.212,0.188   l-40.035,12.726l12.924-38.129c0.418-1.234,0.209-2.595-0.561-3.647c-14.924-20.392-22.813-44.485-22.813-69.677   c0-65.543,53.754-118.867,119.826-118.867c66.064,0,119.812,53.324,119.812,118.867   C276.546,215.678,222.799,268.994,156.734,268.994z" />
      </g>
    </svg>
  );
}

const isMobile = () => {
  if (typeof window !== "undefined") {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }
  return false;
};

function ModeWhatsapp() {
  const { isOpen, isShow, toggle, isPreviewVisible } = useLauncher();
  const apiBase = useAppSelector((state) => state.config.data.apiBase);
  const integration = useAppSelector((state) => state.config.data.whatsappId);
  const { t } = useTranslation();
  const qrCode = apiBase + `/v1/whatsapp/${integration}/qr`;
  const link = apiBase + `/v1/whatsapp/${integration}/url`;

  const onBubbleClick = () => {
    if (isMobile()) {
      window.location.href = link;
    } else {
      toggle();
    }
  };
  return (
    // Require that SDK isLoaded / processed queue -- because frame might be manually hiden by SDK.
    <div className={showElement(isShow) + " antialiased"}>
      <GrayedCorner isVisible={isShow && (isOpen || isPreviewVisible)} />
      <BubbleIconContainer
        isOpen={isOpen}
        onClick={onBubbleClick}
        bgClassName="bg-gradient-to-b to-[#57bf3b] from-[#6cee6c]"
      >
        <WhatsAppIcon />
      </BubbleIconContainer>
      {/* Fold Out */}
      <Frame isOpen={isOpen} widthLimit={280} heightLimit={350}>
        <div className="flex flex-col h-full w-full">
          <div className="flex flex-row justify-between w-full p-4">
            <h3 className="text-lg font-semibold text-gray-800">
              {t("whatsapp.title")}
            </h3>
            <button
              onClick={toggle}
              className="right-0 text-gray-500 hover:text-gray-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-grow flex flex-col items-center justify-center w-full px-4">
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-full h-full"
            >
              <img
                src={qrCode}
                alt="WhatsApp QR Code"
                className="w-full max-w-[224px] object-contain mx-auto"
              />
            </a>
          </div>
          <p className="text-sm text-gray-600 text-center p-4">
            <Trans
              i18nKey="whatsapp.instructions"
              components={{
                bold: <span className="font-bold" />,
              }}
            />
          </p>
        </div>
      </Frame>
    </div>
  );
}

function PositionMode({
  mode,
  children,
}: {
  mode: string;
  children: ReactNode;
}) {
  if (mode === "launcher") {
    return <ModeLauncher>{children}</ModeLauncher>;
  } else if (mode === "inline") {
    return <ModeInline>{children}</ModeInline>;
  } else if (mode.includes("fullscreen")) {
    return <ModeFullscreen>{children}</ModeFullscreen>;
  } else if (mode === "whatsapp") {
    return <ModeWhatsapp />;
  } else {
    throw new Error(`Invalid mode: ${mode}`);
  }
}

export default PositionMode;
